import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';

import type { AgendaEvent } from '@amumin/api-client';

import { api } from '../api.js';
import type { MemberLite } from '../lib/agenda.js';
import { dataVersion } from '../state/session.js';
import { EventEditor, type EditorTarget } from './EventEditor.js';
import { EventSheet } from './EventSheet.js';

/** Detail-sheet + editor voor afspraken; gedeeld door Vandaag, Agenda en Ritten. */
export function useEventDialogs() {
  const sheet = useSignal<AgendaEvent | null>(null);
  const editor = useSignal<EditorTarget | null>(null);
  const dayEvents = useSignal<ReadonlyArray<AgendaEvent>>([]);

  const openSheet = (ev: AgendaEvent, sameDay: ReadonlyArray<AgendaEvent> = []) => {
    dayEvents.value = sameDay;
    sheet.value = ev;
  };
  /** 0022: dezelfde keer opnieuw ophalen (na overnemen of ongedaan maken) zodat het blad klopt. */
  const refreshSheet = async (ev: AgendaEvent) => {
    const date = ev.date ?? ev.original_date;
    if (!date) return;
    try {
      const res = await api.app.agenda.get(date, date);
      const fresh = res.days
        .flatMap((d) => d.events)
        .find((e) => e.occurrence_id === ev.occurrence_id);
      if (fresh && sheet.value?.occurrence_id === ev.occurrence_id) sheet.value = fresh;
    } catch {
      // Niet erg: de agenda zelf ververst via bumpData.
    }
  };
  // Na elke mutatie en na een versieconflict (409: "De lijst is opnieuw geladen") ook het open blad
  // opnieuw ophalen: anders bleef het de oude rijder tonen terwijl de ander al had overgenomen
  // (review c-fs r2). `refresh` doet hetzelfde voor een live poke van de pagina (useLive).
  const refresh = () => {
    const current = sheet.value;
    if (current) void refreshSheet(current);
  };
  const version = dataVersion.value;
  useEffect(() => {
    if (version > 0) refresh();
  }, [version]);
  const openEditor = (target: EditorTarget) => {
    sheet.value = null;
    editor.value = target;
  };

  const render = (members: ReadonlyArray<MemberLite>) => (
    <>
      <EventSheet
        occurrence={sheet.value}
        dayEvents={dayEvents.value}
        members={members}
        onClose={() => (sheet.value = null)}
        onEdit={(occ) => openEditor({ kind: 'edit', occurrence: occ })}
        onChanged={(occ) => void refreshSheet(occ)}
      />
      <EventEditor target={editor.value} members={members} onClose={() => (editor.value = null)} />
    </>
  );

  return { openSheet, openEditor, refresh, render };
}
